import {
  AfterViewInit,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular'
import { CalendarOptions, DatesSetArg, EventClickArg } from '@fullcalendar/core'
// Imports des plugins standards uniquement
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import frLocale from '@fullcalendar/core/locales/fr'
import { CardModule } from 'primeng/card'
import { CheckboxModule } from 'primeng/checkbox'
import {
  FormBuilder,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms'
import { UserService } from '../../../users/services/user.service'
import { UserModel } from '../../../../core/models/user.model'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { ChipModule } from 'primeng/chip'
import {
  combineLatest,
  debounceTime,
  finalize,
  map,
  startWith,
  Subject,
  switchMap,
  zip,
  tap,
  BehaviorSubject,
  filter,
  Subscription,
  of,
} from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { InterventionsService } from '../../../interventions/services/interventions.service'
import { EventsService } from '../../../events/services/events.service'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { AsyncPipe, CommonModule } from '@angular/common'
import { CustomCheckboxComponent } from '../../../../core/components/custom-checkbox/custom-checkbox.component'
import { MlSocieties } from '../../../../core/enums/ml-societies'
import { DatePickerModule } from 'primeng/datepicker'
import { DialogModule } from 'primeng/dialog'
import { InterventionCalendarModel } from '../../../../core/models/intervention-calendar.model'
import { MessageService } from 'primeng/api'
import { ToastService } from '../../../../shared/services/toast.service'
import { DateTools } from '../../../../core/tools/date-tools'
import { RouteTools } from '../../../../core/tools/route-tools'
import { InputGroupModule } from 'primeng/inputgroup'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { ButtonModule } from 'primeng/button'
import { ContextMenuModule } from 'primeng/contextmenu'
import { MenuItem, ConfirmationService } from 'primeng/api'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { ShiftOriginModel } from '../../../../core/models/shift-origin.model'
import { SelectModule } from 'primeng/select'
import { RouteDateSelectorComponent } from '../route-date-selector/route-date-selector.component'
import { EventDetailsModalComponent } from '../../../interventions/components/event-details-modal/event-details-modal.component'
import { ShiftModalComponent } from '../../../interventions/components/shift-modal/shift-modal.component'
import { UnprogrammingModalComponent } from '../../../interventions/components/unprogramming-modal/unprogramming-modal.component'
import { NewModalComponent } from '../../../events/components/new-modal/new-modal.component'
import { EditModalComponent } from '../../../events/components/edit-modal/edit-modal.component'

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FullCalendarModule,
    DatePickerModule, // Already present, keep it
    CardModule,
    CheckboxModule,
    ReactiveFormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ChipModule,
    ProgressSpinnerModule,
    AsyncPipe,
    CustomCheckboxComponent,
    DialogModule,
    InputGroupModule,
    InputGroupAddonModule,
    ButtonModule,
    FormsModule,
    ContextMenuModule,
    ConfirmDialogModule,
    SelectModule,
    UnprogrammingModalComponent,
    RouteDateSelectorComponent,
    ShiftModalComponent,
    EventDetailsModalComponent,
    NewModalComponent,
    EditModalComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
})
export class CalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  private fb = inject(FormBuilder)
  private userServices = inject(UserService)
  private interventionsService = inject(InterventionsService)
  private eventsService = inject(EventsService)
  private toastService = inject(ToastService)
  private confirmationService = inject(ConfirmationService)

  public showNewModal: boolean = false

  @ViewChild('calendar') calendar!: FullCalendarComponent
  @ViewChild('contextMenu') contextMenu: any // ContextMenu de PrimeNG

  public selectedEvent: any = null
  public showEventDialog: boolean = false
  public selectedEventDetails: any = {}

  // Clé pour le stockage local des utilisateurs sélectionnés
  private readonly SELECTED_USERS_STORAGE_KEY = 'calendar_selected_users'

  // Stocker temporairement les IDs des utilisateurs à restaurer
  private storedUserIds: number[] = []

  // Ajouter un BehaviorSubject pour stocker les dates actuelles
  private currentDates$ = new BehaviorSubject<{ start: Date; end: Date }>({
    start: new Date(),
    end: new Date(new Date().setDate(new Date().getDate() + 7)),
  })

  // Ajoutons des propriétés pour limiter la sélection de dates à la semaine visible
  public visibleStartDate: Date = new Date()
  public visibleEndDate: Date = new Date(
    new Date().setDate(new Date().getDate() + 7)
  )

  // Variables pour le menu contextuel
  public contextMenuItems: MenuItem[] = []
  public selectedContextEvent: any = null

  // Variables pour le dialogue de déprogrammation
  public showUnprogramDialog: boolean = false
  public shiftOrigins: ShiftOriginModel[] = []
  public loadingShiftOrigins: boolean = false

  // Ajouter une variable pour suivre l'état de soumission
  public unprogramSubmitting: boolean = false

  // Variables pour le décalage d'événement
  public showShiftDialog: boolean = false
  public shiftSubmitting: boolean = false

  // Ajouter une propriété pour stocker tous les techniciens
  public allTechnicians: UserModel[] = []

  // Ajouter des variables pour le dialogue de suppression récurrent
  public showRecurrenceDeleteDialog: boolean = false
  public selectedDeleteScope: 'this' | 'all' | 'thisAndFuture' | null = null

  // Variables pour la modale d'édition
  public showEditModal: boolean = false

  calendarOptions: CalendarOptions = {
    selectable: true,
    initialView: 'timeGridWeek',
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    locale: frLocale,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek',
    },
    aspectRatio: 0,
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00',
    slotDuration: '00:30:00',
    slotLabelInterval: '00:30:00',
    snapDuration: '00:15:00', // Pour la précision des événements
    slotLabelFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    },
    nowIndicator: true,
    allDaySlot: true,
    handleWindowResize: true,
    height: 'auto', // Permettre au calendrier de s'adapter
    contentHeight: 'auto', // S'assurer que le contenu s'adapte correctement
    datesSet: (datesSetArg) => {
      this.datesSet$.next(datesSetArg)
      this.currentDates$.next({
        start: datesSetArg.start,
        end: datesSetArg.end,
      })
      this.visibleStartDate = datesSetArg.start
      this.visibleEndDate = datesSetArg.end
    },
    eventClick: (clickInfo: EventClickArg) => {
      this.handleEventClick(clickInfo)
    },
    eventContent: (arg) => {
      const locality = arg.event.extendedProps?.['locality'] || ''
      const postalCode =
        arg.event.extendedProps?.['data']?.workSitePostalCode || ''
      const title = arg.event.title || 'Sans titre'

      const timeText = arg.timeText || ''

      // Créer le contenu HTML personnalisé
      const container = document.createElement('div')
      container.className = 'fc-event-main-custom'

      const timeDiv = document.createElement('div')
      timeDiv.className = 'fc-event-time-custom'
      timeDiv.textContent = timeText

      const titleDiv = document.createElement('div')
      titleDiv.className = 'fc-event-title-custom'
      titleDiv.textContent = title

      container.appendChild(timeDiv)
      container.appendChild(titleDiv)

      if (postalCode || locality) {
        const localityDiv = document.createElement('div')
        localityDiv.className = 'fc-event-locality-custom'
        const locationText = [postalCode, locality].filter(Boolean).join(' ')
        localityDiv.textContent = locationText
        container.appendChild(localityDiv)
      }

      return { domNodes: [container] }
    },
    eventDidMount: (info) => {
      // Vérifier si l'événement est récurrent et ajouter une classe CSS
      const isRecurrent =
        info.event.extendedProps?.['data']?.isReccurent ||
        info.event.extendedProps?.['data']?.recurrenceRule

      if (isRecurrent) {
        info.el.classList.add('fc-event-recurrent')
      }

      info.el.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        this.selectedContextEvent = info.event

        // Construire le menu contextuel selon le type d'événement
        const eventType =
          info.event.extendedProps?.['data']?.resource || 'event'
        this.contextMenuItems = this.buildContextMenuForEvent(eventType)

        this.contextMenu.show(e)
      })
    },
    editable: true,
    eventDrop: this.handleEventDrop.bind(this),
    eventResize: this.handleEventResize.bind(this),
    select: this.handleSelect.bind(this),
  }

  public newModalStartTime: Date = new Date()
  public newModalDuration: Date = new Date(0)

  handleSelect(selectInfo: any) {
    const start = selectInfo.start
    const end = selectInfo.end

    const startDate = DateTools.toLocalISOString(start)
    const endDate = DateTools.toLocalISOString(end)
    const duration = DateTools.calcTimeDifference(
      new Date(startDate),
      new Date(endDate)
    )

    this.newModalStartTime = new Date(startDate)
    this.newModalDuration = new Date(duration)

    this.showNewModal = true
  }

  mlSocieties = Object.values(MlSocieties)
  public users$
  public filtersForm = this.fb.group({
    societyKeys: [[]],
    search: '',
  })
  public usersForm = this.fb.group({
    users: this.fb.control<UserModel[]>([]),
  })
  public loadingUsers = false
  public loadingEvents = false
  public datesSet$ = new Subject<DatesSetArg>()
  public fullCalendarEvents$

  // Ajout d'un formControl pour la recherche d'événements
  public eventsSearchForm = this.fb.group({
    searchEvents: [''],
  })

  // BehaviorSubject pour stocker tous les événements non filtrés
  public allEvents$ = new BehaviorSubject<any[]>([])

  // Variable pour stocker les événements filtrés
  public filteredEvents$ = new BehaviorSubject<any[]>([])

  // BehaviorSubject pour suivre les utilisateurs sélectionnés
  private selectedUsers$ = new BehaviorSubject<UserModel[]>([])

  // Ajouter un tableau pour stocker les souscriptions
  private subscriptions: Subscription[] = []

  // Propriétés pour la sélection de date d'itinéraire
  public showDateSelector: boolean = false
  public selectedTechnicianForRoute: UserModel | null = null
  public selectedDateForRoute: Date | null = null
  public minDateForRoute: Date = new Date(
    new Date().setMonth(new Date().getMonth() - 3)
  ) // 3 mois en arrière
  public maxDateForRoute: Date = new Date(
    new Date().setMonth(new Date().getMonth() + 3)
  ) // 3 mois en avant

  constructor() {
    // 1. Charger d'abord les IDs des utilisateurs sauvegardés
    this.loadSelectedUsersFromStorage()

    // 2. Observer les changements de filtres et synchroniser avec les utilisateurs stockés
    this.users$ = this.filtersForm.valueChanges.pipe(
      startWith(this.filtersForm.getRawValue()),
      debounceTime(500),
      takeUntilDestroyed(),
      switchMap((filters) => this.getUsers(filters)),
      // 3. Synchroniser avec les utilisateurs stockés quand la liste est chargée
      tap((users) => {
        if (users && users.length > 0 && this.storedUserIds.length > 0) {
          this.syncStoredUsersWithAvailableUsers(users)
        }
      })
    )

    // Surveiller les changements dans la sélection d'utilisateurs pour les enregistrer
    this.usersForm
      .get('users')
      ?.valueChanges.pipe(
        takeUntilDestroyed(),
        debounceTime(500) // Attendre un peu avant d'enregistrer pour éviter les enregistrements trop fréquents
      )
      .subscribe((selectedUsers) => {
        if (selectedUsers) {
          this.saveSelectedUsersToStorage(selectedUsers)
          // Mettre à jour le BehaviorSubject des utilisateurs sélectionnés
          this.selectedUsers$.next(selectedUsers)
        }
      })

    // Modifier notre flux pour utiliser le BehaviorSubject des utilisateurs sélectionnés
    this.fullCalendarEvents$ = combineLatest([
      this.currentDates$, // Utiliser currentDates$ au lieu de datesSet$
      this.selectedUsers$,
    ]).pipe(
      takeUntilDestroyed(),
      switchMap(([dates, selectedUsers]) => {
        const params = {
          users: selectedUsers?.map((user) => user.id) ?? [],
          dateFrom: this.formatDate(dates.start),
          dateTo: this.formatDate(dates.end),
        }
        return this.getEventsAndInterventionsForCalendar(params)
      }),
      tap((events) => {
        this.allEvents$.next(events)
        this.applyEventsFilter()
      })
    )

    // IMPORTANT: Souscrire explicitement à l'observable pour s'assurer qu'il est exécuté
    this.subscriptions.push(this.fullCalendarEvents$.subscribe())

    // Forcer une première émission pour les utilisateurs sélectionnés (même vide)
    this.selectedUsers$.next([])

    // Initialiser les options du menu contextuel
    this.initContextMenu()

    // Charger les origines de décalage
    this.loadShiftOrigins()
  }

  ngOnInit(): void {}

  // Ajouter AfterViewInit pour s'assurer que le calendrier est initialisé
  ngAfterViewInit(): void {
    // Laisser le temps au calendrier de s'initialiser complètement
    setTimeout(() => {
      this.initializeCalendar()
    }, 100)
  }

  // Nouvelle méthode pour initialiser le calendrier
  private initializeCalendar(): void {
    const api = this.calendar?.getApi()
    if (api) {
      // Obtenir les dates visibles actuellement dans le calendrier
      const view = api.view
      const start = view.activeStart
      const end = view.activeEnd

      // Mettre à jour notre BehaviorSubject avec ces dates
      this.currentDates$.next({ start, end })
    } else {
      // Fallback: utiliser les dates par défaut
      // Ne rien faire car le BehaviorSubject a déjà une valeur initiale
    }
  }

  // Ajouter une méthode pour nettoyer les souscriptions
  ngOnDestroy(): void {
    // Nettoyer toutes les souscriptions manuelles
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  getUsers(
    filters: Partial<{
      societyKeys: string[] | null
      search: string | null
    }> = {}
  ) {
    this.loadingUsers = true
    return this.userServices
      .getUsersWithFilters(filters)
      .pipe(finalize(() => (this.loadingUsers = false)))
  }

  getEventsAndInterventionsForCalendar(params: {
    users: number[]
    dateFrom: string
    dateTo: string
  }) {
    // Vérifier s'il y a des utilisateurs sélectionnés
    if (!params.users || params.users.length === 0) {
      // Si aucun utilisateur sélectionné, retourner un tableau vide
      // sans déclencher de chargement
      return of([])
    }

    this.loadingEvents = true
    return zip([
      this.interventionsService.getInterventions(params),
      this.eventsService.getEvents(params),
    ]).pipe(
      finalize(() => (this.loadingEvents = false)),

      tap(([interventions, events]) => {
        console.log('✅ Interventions:', interventions)
        console.log('✅ Events:', events)
      }),

      map(([interventions, events]) => [...interventions, ...events])
    )
  }

  gotoDate(date: Date) {
    this.calendar.getApi().gotoDate(date)
  }

  //format date to yyyy-mm-dd
  formatDate(date: Date) {
    return DateTools.formatDateForAPI(date)
  }

  /**
   * Enregistre les utilisateurs sélectionnés dans le localStorage
   */
  private saveSelectedUsersToStorage(users: UserModel[]): void {
    if (!users) return

    try {
      // Stocker uniquement les informations essentielles des utilisateurs
      console.log(users)

      const simplifiedUsers = users.map((user) => ({
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        initials: user.initials || this.generateInitials(user),
        erpSource: user.erpSource || 'nrj',
        // Ajouter d'autres propriétés essentielles au besoin
      }))

      localStorage.setItem(
        this.SELECTED_USERS_STORAGE_KEY,
        JSON.stringify(simplifiedUsers)
      )
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des utilisateurs:', error)
    }
  }

  /**
   * Charge les IDs des utilisateurs sélectionnés depuis le localStorage
   */
  private loadSelectedUsersFromStorage(): void {
    try {
      const storedUsersJson = localStorage.getItem(
        this.SELECTED_USERS_STORAGE_KEY
      )

      if (storedUsersJson) {
        const storedUsers = JSON.parse(storedUsersJson)

        if (Array.isArray(storedUsers) && storedUsers.length > 0) {
          // Au lieu de mettre à jour directement le formulaire,
          // stocke les IDs pour les synchroniser plus tard
          this.storedUserIds = storedUsers.map((user) => user.id)
        }
      }
    } catch (error) {
      // En cas d'erreur, supprimer les données potentiellement corrompues
      localStorage.removeItem(this.SELECTED_USERS_STORAGE_KEY)
    }
  }

  /**
   * Synchronise les utilisateurs stockés avec la liste des utilisateurs disponibles
   */
  private syncStoredUsersWithAvailableUsers(availableUsers: UserModel[]): void {
    if (!this.storedUserIds.length) return

    // Trouve les utilisateurs correspondants entre la liste chargée et les IDs stockés
    const selectedUsers = availableUsers.filter((user) =>
      this.storedUserIds.includes(user.id)
    )

    if (selectedUsers.length > 0) {
      // Met à jour le formulaire avec les utilisateurs disponibles correspondants
      this.usersForm.patchValue({ users: selectedUsers })

      // Mettre à jour aussi le BehaviorSubject
      this.selectedUsers$.next(selectedUsers)

      // Réinitialise les IDs stockés pour éviter une double synchronisation
      this.storedUserIds = []
    }
  }

  /**
   * Génère les initiales à partir du prénom et du nom
   */
  private generateInitials(user: UserModel): string {
    if (!user.firstname || !user.lastname) return '??'
    return (user.firstname.charAt(0) + user.lastname.charAt(0)).toUpperCase()
  }

  removeUser(user: UserModel) {
    const selectedUsers = this.usersForm.controls.users.value ?? []
    const updatedUsers = selectedUsers.filter((u) => u.id !== user.id)
    this.usersForm.controls.users.setValue(updatedUsers)
    // La sauvegarde est gérée par la souscription aux changements de valeur
  }

  handleEventClick(clickInfo: EventClickArg) {
    // Prevent the default action (navigation)
    clickInfo.jsEvent.preventDefault()

    // Create a composite object with both the event core properties and extended props
    const eventData = {
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
      ...clickInfo.event.extendedProps,
    }

    console.log(clickInfo.event.extendedProps)

    this.selectedEvent = eventData
    this.prepareEventDetailsForDisplay(eventData)
    this.showEventDialog = true
  }

  prepareEventDetailsForDisplay(eventData: any) {
    // Format the event data for display
    this.selectedEventDetails = {}

    if (eventData.data?.resource === 'intervention') {
      // It's an intervention
      const intervention = eventData.data

      // Préparation des techniciens avec les liens d'itinéraire
      const technicianDetails = intervention.usersTechnicians.map(
        (tech: any) => {
          return {
            id: tech.id,
            name: `${tech.firstname} ${tech.lastname}`,
            initials:
              tech.initials ||
              tech.firstname?.charAt(0) + tech.lastname?.charAt(0) ||
              '??',
            // Utiliser le nouvel outil pour générer l'URL d'itinéraire
            routeUrl: this.generateDailyRouteUrl(tech, eventData.start),
          }
        }
      )

      this.selectedEventDetails = {
        type: 'Intervention',
        title: eventData.title || 'Sans titre',
        dateRange: this.formatDateRange(eventData.start, eventData.end),
        status: intervention.interventionStatus?.name || 'Non défini',
        address: this.formatAddress(intervention),
        technicians: this.formatTechnicians(intervention.usersTechnicians),
        technicianDetails: technicianDetails, // ajouter les détails complets
        description: intervention.description || 'Aucune description',
        statusColor: intervention.interventionStatus?.tagColor,
        // Add additional fields that might be useful
        mlRef: this.extractMlRef(intervention.description),
        workSiteDepartment: intervention.workSiteDepartment,
        // Ajouter les coordonnées GPS pour les utiliser dans les liens
        coordinates: {
          latitude: intervention.workSiteLatitude,
          longitude: intervention.workSiteLongitude,
        },
      }
    } else {
      // Handle other event types if needed
      this.selectedEventDetails = {
        type: 'Événement',
        title: eventData.title || 'Sans titre',
        dateRange: this.formatDateRange(eventData.start, eventData.end),
        description: eventData.description || 'Aucune description',
      }
    }
  }

  // Helper method to extract ML reference from description
  extractMlRef(description: string): string | null {
    if (!description) return null

    // Try to match a pattern like ML24012121 - common ML reference format
    const match = description.match(/\bML\d+\b/)
    return match ? match[0] : null
  }

  formatDateRange(
    start: string | Date | null,
    end: string | Date | null
  ): string {
    return DateTools.formatDateForDisplay(start, end)
  }

  formatAddress(intervention: any): string {
    if (!intervention) return 'Adresse non disponible'

    const parts = [
      intervention.workSiteAddress,
      intervention.workSitePostalCode,
      intervention.workSiteLocality,
    ].filter(Boolean)

    return parts.join(', ')
  }

  // Gestion du déplacement d'un événement
  handleEventDrop(info: any): void {
    const { event } = info
    console.log(event.extendedProps?.data?.resource)

    const interventionId = event.extendedProps.data.id

    const erpSource = event.extendedProps.data.erpSource

    const startDate = DateTools.toLocalISOString(event.start)
    const endDate = DateTools.toLocalISOString(event.end)
    const duration = DateTools.calcTimeDifference(
      new Date(startDate),
      new Date(endDate)
    )
    this.loadingEvents = true

    const params = {
      users: this.usersForm.value.users?.map((user) => user.id) ?? [],
      startDate: startDate,
      erpSource: erpSource,
      duration: duration.toTimeString(),
    }

    if (event.extendedProps?.data?.resource === 'intervention') {
      this.interventionsService
        .updateDate(interventionId, params)
        .pipe(finalize(() => (this.loadingEvents = false)))
        .subscribe({
          next: () => {
            this.toastService.showSuccess(
              'Intervention mise à jour avec succès'
            )
          },
          error: (err) => {
            info.revert()
          },
        })
    } else if (event.extendedProps?.data?.resource === 'event') {
      this.eventsService
        .updateDate(interventionId, params)
        .pipe(finalize(() => (this.loadingEvents = false)))
        .subscribe({
          next: () => {
            this.toastService.showSuccess('Evenement mise à jour avec succès')
          },
          error: (err) => {
            info.revert()
          },
        })
    }
  }

  // Gestion du redimensionnement d'un événement
  handleEventResize(info: any): void {
    // Réutiliser la même logique que pour le déplacement
    this.handleEventDrop(info)
  }

  // Méthode pour filtrer les événements en fonction du terme de recherche
  public applyEventsFilter(): void {
    const searchTerm =
      this.eventsSearchForm.get('searchEvents')?.value?.toLowerCase() || ''

    if (!searchTerm.trim()) {
      // Si pas de recherche, afficher tous les événements
      this.filteredEvents$.next(this.allEvents$.value)
      return
    }

    const filteredEvents = this.allEvents$.value.filter((event) => {
      // Recherche dans le titre
      if (event.title?.toLowerCase().includes(searchTerm)) {
        return true
      }

      // Recherche dans la description
      if (event.description?.toLowerCase().includes(searchTerm)) {
        return true
      }

      // Recherche dans l'adresse pour les interventions
      if (event.data?.resource === 'intervention') {
        const intervention = event.data

        // Recherche dans l'adresse
        const address = [
          intervention.workSiteAddress,
          intervention.workSitePostalCode,
          intervention.workSiteLocality,
          intervention.workSiteDepartment,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        if (address.includes(searchTerm)) {
          return true
        }

        // Recherche parmi les techniciens
        if (
          intervention.usersTechnicians?.some((tech: any) =>
            `${tech.firstname} ${tech.lastname}`
              .toLowerCase()
              .includes(searchTerm)
          )
        ) {
          return true
        }
      }

      return false
    })

    this.filteredEvents$.next(filteredEvents)
  }

  // Méthode pour appliquer la recherche immédiatement (touche Entrée)
  applySearchImmediate(event: Event): void {
    // Empêcher le rechargement de la page sur submit du formulaire
    event.preventDefault()

    // Appliquer le filtre immédiatement
    this.applyEventsFilter()

    // Retirer le focus du champ pour améliorer l'expérience utilisateur
    const activeElement = document.activeElement as HTMLElement
    if (activeElement) {
      activeElement.blur()
    }
  }

  // Méthode pour réinitialiser la recherche
  resetEventSearch(): void {
    this.eventsSearchForm.get('searchEvents')?.setValue('')
    this.applyEventsFilter()
  }

  /**
   * Vérifie si un technicien a des interventions dans la période visible
   */
  checkTechnicianHasInterventions(technician: UserModel): boolean {
    return RouteTools.technicianHasInterventions(
      technician,
      this.allEvents$.value
    )
  }

  /**
   * Ouvre le dialogue de sélection de date pour l'itinéraire d'un technicien
   */
  openDateSelector(event: Event, technician: UserModel): void {
    // Empêcher la propagation pour éviter de sélectionner la checkbox
    event.preventDefault()
    event.stopPropagation()

    // Définir le technicien sélectionné
    this.selectedTechnicianForRoute = technician

    // Vérifier si le technicien a des interventions
    const hasInterventions = this.checkTechnicianHasInterventions(technician)
    if (!hasInterventions) {
      this.toastService.showError(
        `Aucune intervention trouvée pour ${technician.firstname} ${technician.lastname} dans la période affichée.`
      )
      return
    }

    // Initialiser à la date du jour ou à la date visible si le jour actuel n'est pas dans la période
    const today = new Date()
    if (today >= this.visibleStartDate && today <= this.visibleEndDate) {
      this.selectedDateForRoute = today
    } else {
      this.selectedDateForRoute = new Date(this.visibleStartDate)
    }

    // Limiter le sélecteur de date à la semaine visible
    this.minDateForRoute = this.visibleStartDate
    this.maxDateForRoute = this.visibleEndDate

    // Afficher le dialogue
    this.showDateSelector = true
  }

  /**
   * Vérifie si un utilisateur est actuellement sélectionné
   */
  isUserSelected(user: UserModel): boolean {
    if (!this.usersForm?.value?.users) {
      return false
    }

    return this.usersForm.value.users.some(
      (selectedUser: UserModel) => selectedUser.id === user.id
    )
  }

  /**
   * Génère et ouvre l'itinéraire pour le technicien à la date sélectionnée
   */
  generateAndOpenRoute(): void {
    if (!this.selectedTechnicianForRoute || !this.selectedDateForRoute) {
      return
    }

    // Générer l'URL de l'itinéraire
    const routeUrl = this.generateDailyRouteUrl(
      this.selectedTechnicianForRoute,
      this.selectedDateForRoute
    )

    // Fermer le dialogue
    this.showDateSelector = false

    // Ouvrir l'URL dans un nouvel onglet si elle existe
    if (routeUrl) {
      window.open(routeUrl, '_blank')
    } else {
      // Informer l'utilisateur qu'il n'y a pas d'interventions pour cette date
      this.toastService.showError(
        `Aucune intervention trouvée pour ${this.selectedTechnicianForRoute.firstname} ${this.selectedTechnicianForRoute.lastname} à cette date.`
      )
    }
  }

  /**
   * Initialise les options du menu contextuel
   */
  private initContextMenu(): void {
    // Le menu sera maintenant construit dynamiquement
    this.contextMenuItems = []
  }

  /**
   * Construit les options du menu contextuel selon le type d'événement
   */
  private buildContextMenuForEvent(eventType: string): MenuItem[] {
    const baseItems: MenuItem[] = []

    if (eventType === 'event') {
      // Pour les événements simples, proposer "Modifier"
      baseItems.push({
        label: 'Modifier',
        icon: 'pi pi-pencil',
        command: () => {
          this.openEditDialog()
        },
      })

      // Pour les événements simples, proposer "Supprimer"
      baseItems.push({
        label: 'Supprimer',
        icon: 'pi pi-trash',
        command: () => {
          this.handleDeleteEvent()
        },
      })
    } else if (eventType === 'intervention') {
      // Pour les interventions, proposer "Décaler"
      baseItems.push({
        label: 'Décaler',
        icon: 'pi pi-calendar',
        command: () => {
          this.openRescheduleDialog()
        },
      })

      // Pour les interventions, proposer "Déprogrammer"
      baseItems.push({
        label: 'Déprogrammer',
        icon: 'pi pi-trash',
        command: () => {
          this.handleDeleteEvent()
        },
      })
    }

    return baseItems
  }

  /**
   * Gère la suppression d'un événement selon son type
   */
  handleDeleteEvent(): void {
    if (!this.selectedContextEvent) return

    const eventType = this.selectedContextEvent.extendedProps?.data?.resource

    if (eventType === 'event') {
      // Pour les événements simples, appeler directement deleteEventDirectly
      // qui gérera les cas avec et sans récurrence
      this.deleteEventDirectly()
    } else if (eventType === 'intervention') {
      // Pour les interventions, utiliser la modale de déprogrammation
      this.confirmUnprogramEvent()
    }
  }

  /**
   * Supprime directement un événement de type "event"
   */
  deleteEventDirectly(): void {
    if (!this.selectedContextEvent) return

    const eventId = this.selectedContextEvent.extendedProps?.data?.id
    console.log(this.selectedContextEvent.extendedProps?.data)
    const recurrenceRule =
      this.selectedContextEvent.extendedProps?.data?.recurrenceRule

    if (!eventId) {
      this.toastService.showError('Impossible de supprimer cet événement')
      return
    }

    const erpSource = this.selectedContextEvent.extendedProps?.data?.erpSource
    if (!erpSource) {
      this.toastService.showError('Source ERP manquante pour la suppression')
      return
    }

    // Si l'événement a une récurrence, proposer les options de suppression
    if (recurrenceRule) {
      this.showRecurrenceDeleteOptions()
    } else {
      // Suppression simple pour les événements non récurrents
      this.confirmSimpleEventDelete()
    }
  }

  /**
   * Confirmation pour supprimer un événement simple (non récurrent)
   */
  private confirmSimpleEventDelete(): void {
    this.confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir supprimer cet événement ?',
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept: () => {
        this.performEventDeletion('this')
      },
    })
  }

  /**
   * Affiche les options de suppression pour un événement récurrent
   */
  private showRecurrenceDeleteOptions(): void {
    this.selectedDeleteScope = null
    this.showRecurrenceDeleteDialog = true
  }

  /**
   * Ferme le dialogue de suppression récurrent
   */
  closeRecurrenceDeleteDialog(): void {
    this.showRecurrenceDeleteDialog = false
    this.selectedDeleteScope = null
  }

  /**
   * Sélectionne le scope de suppression et ferme le dialogue
   */
  selectDeleteScope(scope: 'this' | 'all' | 'thisAndFuture'): void {
    this.selectedDeleteScope = scope
    this.showRecurrenceDeleteDialog = false
    this.performEventDeletion(scope)
  }

  /**
   * Effectue la suppression selon le type sélectionné
   */
  private performEventDeletion(
    deleteScope: 'this' | 'all' | 'thisAndFuture'
  ): void {
    if (!this.selectedContextEvent) return

    const eventId = this.selectedContextEvent.extendedProps?.data?.id
    const parentEventId =
      this.selectedContextEvent.extendedProps?.data?.parentEventId
    const erpSource = this.selectedContextEvent.extendedProps?.data?.erpSource
    const eventDate = this.selectedContextEvent.start
    const recurrenceRule =
      this.selectedContextEvent.extendedProps?.data?.recurrenceRule

    this.loadingEvents = true

    const deleteParams = {
      body: {
        erpSource: erpSource,
        delete_scope: deleteScope,
        eventDate:
          deleteScope === 'thisAndFuture'
            ? this.formatDateToISO(eventDate)
            : undefined,
      },
    }

    const deleteObservable = recurrenceRule
      ? this.eventsService.deleteWithRecurrence(
          parentEventId ?? eventId,
          deleteParams
        )
      : this.eventsService.delete(eventId, deleteParams)

    deleteObservable
      .pipe(
        finalize(() => {
          this.loadingEvents = false
        })
      )
      .subscribe({
        next: () => {
          let successMessage = 'Événement supprimé avec succès'
          switch (deleteScope) {
            case 'this':
              successMessage = 'Occurrence supprimée avec succès'
              break
            case 'all':
              successMessage = 'Toutes les occurrences supprimées avec succès'
              break
            case 'thisAndFuture':
              successMessage =
                'Cette occurrence et les futures supprimées avec succès'
              break
          }
          this.toastService.showSuccess(successMessage)
          this.refreshEvents()
        },
        error: (err) => {
          this.toastService.showError('Erreur lors de la suppression')
        },
      })
  }

  /**
   * Formate une date en ISO string
   */
  private formatDateToISO(date: Date): string {
    return date.toISOString()
  }

  /**
   * Ouvre le dialogue d'édition d'événement
   */
  openEditDialog(): void {
    if (!this.selectedContextEvent) return

    // Vérifier que c'est bien un événement (pas une intervention)
    const eventType = this.selectedContextEvent.extendedProps?.data?.resource
    if (eventType !== 'event') {
      this.toastService.showError(
        'Seuls les événements peuvent être modifiés via cette fonctionnalité'
      )
      return
    }

    // Ouvrir la modale d'édition
    this.showEditModal = true
  }

  /**
   * Callback appelé lorsque l'événement est modifié avec succès
   */
  onEvenementModifie(): void {
    this.refreshEvents()
  }

  /**
   * Ouvre le dialogue de décalage d'événement
   */
  openRescheduleDialog(): void {
    if (!this.selectedContextEvent) return

    // Charger les origines de décalage si nécessaire
    this.loadShiftOrigins()

    // Charger tous les techniciens si nécessaire
    this.loadAllTechnicians()

    // Afficher la modale de décalage
    this.showShiftDialog = true
  }

  /**
   * Charge tous les techniciens disponibles
   */
  loadAllTechnicians(): void {
    if (this.allTechnicians.length > 0) {
      return // Déjà chargés
    }

    this.userServices
      .getUsersWithFilters({
        // Vous pouvez ajouter des filtres spécifiques ici si nécessaire
        // Par exemple, pour ne récupérer que les techniciens:
        // role: 'technician'
      })
      .subscribe({
        next: (technicians) => {
          this.allTechnicians = technicians
        },
        error: (error) => {
          this.toastService.showError(
            'Erreur lors du chargement des techniciens'
          )
        },
      })
  }

  /**
   * Traite la soumission du formulaire de décalage
   */
  handleShiftSubmit(formData: any): void {
    if (!this.selectedContextEvent) return

    const objectId = this.selectedContextEvent.extendedProps?.data?.id

    if (!objectId) {
      this.toastService.showError('Impossible de décaler cet événement')
      return
    }

    // Activer l'indicateur de chargement
    this.shiftSubmitting = true

    if (
      this.selectedContextEvent.extendedProps?.data?.resource === 'intervention'
    ) {
      // Appeler le service pour décaler l'intervention
      this.interventionsService
        .shift(objectId, formData)
        .pipe(
          finalize(() => {
            // Désactiver l'indicateur de chargement
            this.shiftSubmitting = false
            // Fermer le dialogue après la fin de l'opération,
            // qu'elle soit réussie ou non, pour éviter de confondre l'utilisateur
            this.showShiftDialog = false
          })
        )
        .subscribe({
          next: () => {
            this.toastService.showSuccess('Intervention décalée avec succès')
            this.refreshEvents()
          },
          error: (err) => {
            this.toastService.showError('Erreur lors du décalage')
          },
        })
    } else if (
      this.selectedContextEvent.extendedProps?.data?.resource === 'event'
    ) {
      // Appeler le service pour décaler l'intervention
      this.eventsService
        .shift(objectId, formData)
        .pipe(
          finalize(() => {
            // Désactiver l'indicateur de chargement
            this.shiftSubmitting = false
            // Fermer le dialogue après la fin de l'opération,
            // qu'elle soit réussie ou non, pour éviter de confondre l'utilisateur
            this.showShiftDialog = false
          })
        )
        .subscribe({
          next: () => {
            this.toastService.showSuccess('Intervention décalée avec succès')
            this.refreshEvents()
          },
          error: (err) => {
            this.toastService.showError('Erreur lors du décalage')
          },
        })
    }
  }

  /**
   * Affiche une confirmation avant de déprogrammer une intervention
   */
  confirmUnprogramEvent(): void {
    if (!this.selectedContextEvent) return

    // Charger les origines de décalage
    this.loadShiftOrigins()

    // Ouvrir le dialogue de déprogrammation
    this.showUnprogramDialog = true
  }

  /**
   * Déprogramme une intervention
   */
  unprogramEvent(formData: any): void {
    if (!this.selectedContextEvent) return

    const objectId = this.selectedContextEvent.extendedProps?.data?.id

    if (!objectId) {
      this.toastService.showError(
        'Impossible de déprogrammer cette intervention'
      )
      return
    }

    // Activer l'indicateur de chargement
    this.unprogramSubmitting = true

    // Seules les interventions utilisent cette méthode maintenant
    this.interventionsService
      .deprogram(objectId, formData)
      .pipe(
        finalize(() => {
          // Désactiver l'indicateur de chargement
          this.unprogramSubmitting = false
        })
      )
      .subscribe({
        next: () => {
          this.toastService.showSuccess('Intervention déprogrammée avec succès')
          this.showUnprogramDialog = false

          // Rafraîchir les événements en déclenchant une nouvelle requête API
          this.refreshEvents()
        },
        error: (err) => {
          this.toastService.showError('Erreur lors de la déprogrammation')
        },
      })
  }

  /**
   * Charge les origines de décalage depuis l'API
   */
  loadShiftOrigins(): void {
    // On ne peut pas charger les origines sans interventions sélectionnées
    if (!this.selectedContextEvent) return

    // Récupérer l'origine ERP depuis l'événement sélectionné
    const erpSource = this.selectedContextEvent.extendedProps?.data?.erpSource

    if (!erpSource) {
      return
    }

    this.loadingShiftOrigins = true

    this.interventionsService
      .getShiftOrigins(erpSource)
      .pipe(finalize(() => (this.loadingShiftOrigins = false)))
      .subscribe({
        next: (response: any) => {
          // Convertir la réponse en objets ShiftOriginModel
          if (Array.isArray(response)) {
            this.shiftOrigins = response.map(
              (item) => new ShiftOriginModel(item)
            )
          } else {
            this.shiftOrigins = []
          }
        },
        error: (error) => {
          this.toastService.showError(
            'Erreur lors du chargement des origines de décalage'
          )
          this.shiftOrigins = []
        },
      })
  }

  /**
   * Méthode trackBy pour optimiser le rendu des listes ngFor
   */
  trackById(index: number, user: UserModel): number {
    return user.id
  }

  /**
   * Formats the list of technicians into a readable string.
   * @param technicians Array of technician objects.
   * @returns A formatted string of technician names.
   */
  private formatTechnicians(technicians: any[]): string {
    return technicians
      .map((tech: any) => `${tech.firstname} ${tech.lastname}`)
      .join(', ')
  }

  /**
   * Méthode pour forcer le rafraîchissement des événements du calendrier
   * en déclenchant une nouvelle requête API
   */
  refreshEvents(): void {
    // Récupérer les dates actuelles du calendrier
    const currentDates = this.currentDates$.getValue()

    // Émettre à nouveau les dates actuelles pour déclencher une nouvelle requête API
    this.currentDates$.next({
      start: new Date(currentDates.start),
      end: new Date(currentDates.end),
    })

    // Informer l'utilisateur que les événements sont en cours de rafraîchissement
    this.toastService.showInfo('Actualisation des événements en cours...')
  }

  onEvenementCree() {
    this.refreshEvents()
  }

  // Propriétés pour les itinéraires
  public selectedTechForRoutes: UserModel | null = null
  public currentView: string = 'timeGridWeek'

  /**
   * Récupère les utilisateurs sélectionnés pour l'affichage des itinéraires
   */
  get selectedTechnicians(): UserModel[] {
    return this.usersForm.value.users || []
  }

  /**
   * Récupère les événements actuels du calendrier
   */
  get currentEvents(): any[] {
    return this.allEvents$.value || []
  }

  /**
   * Compte le nombre d'interventions pour un jour et un technicien donnés
   */
  countDayInterventions(date: Date, technician: UserModel | null): number {
    if (
      !technician ||
      !date ||
      !this.currentEvents ||
      this.currentEvents.length === 0
    ) {
      return 0
    }

    return this.currentEvents.filter((event) => {
      const eventDate = new Date(event.start)
      const isSameDay = this.isSameDay(eventDate, date)

      if (!isSameDay) return false

      let isForTechnician = false

      if (
        event.data &&
        event.data.usersTechnicians &&
        Array.isArray(event.data.usersTechnicians)
      ) {
        isForTechnician = event.data.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      if (
        !isForTechnician &&
        event.usersTechnicians &&
        Array.isArray(event.usersTechnicians)
      ) {
        isForTechnician = event.usersTechnicians.some(
          (tech: any) =>
            tech.email === technician.email || tech.id === technician.id
        )
      }

      return isSameDay && isForTechnician
    }).length
  }

  /**
   * Vérifie si un jour spécifique a des interventions pour un technicien donné
   */
  dayHasInterventions(date: Date, technician: UserModel | null): boolean {
    return this.countDayInterventions(date, technician) > 0
  }

  /**
   * Ouvre l'URL de l'itinéraire journalier dans un nouvel onglet
   */
  openDailyRouteUrl(technician: UserModel | any, date: Date): void {
    if (!this.dayHasInterventions(date, technician)) {
      alert(
        "Aucune intervention n'est programmée pour ce technicien à cette date"
      )
      return
    }

    const url = this.generateDailyRouteUrl(technician, date)

    if (!url || url === 'https://www.google.com/maps/dir/') {
      alert('Aucune intervention trouvée pour ce technicien à cette date')
      return
    }

    window.open(url, '_blank')
  }

  /**
   * Génération d'URL de routing journalier pour un technicien
   */
  generateDailyRouteUrl(technician: UserModel | any, date: Date): string {
    if (!technician || !date || !this.currentEvents) {
      return ''
    }

    return RouteTools.generateDailyRouteUrl(
      technician,
      date,
      this.currentEvents
    )
  }

  /**
   * Récupère tous les jours de la semaine visible
   */
  getWeekDays(): {
    date: Date
    shortName: string
    longFormat: string
    isToday: boolean
    hasInterventions: boolean
    interventionCount: number
  }[] {
    const result = []
    const today = new Date()
    const startDate = new Date(this.visibleStartDate)
    const endDate = new Date(this.visibleEndDate)

    const maxDays = 7
    const totalDays = Math.min(
      maxDays,
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      )
    )

    for (let i = 0; i < totalDays; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)

      const shortName = this.getDayShortName(date.getDay())
      const longFormat = this.formatDayForTooltip(date)
      const isToday = this.isSameDay(date, today)

      const technicianToCheck =
        this.selectedTechForRoutes ||
        (this.selectedTechnicians.length === 1
          ? this.selectedTechnicians[0]
          : null)
      const interventionCount = this.countDayInterventions(
        date,
        technicianToCheck
      )
      const hasInterventions = interventionCount > 0

      result.push({
        date,
        shortName,
        longFormat,
        isToday,
        hasInterventions,
        interventionCount,
      })
    }

    return result
  }

  /**
   * Récupère le nom court du jour de la semaine
   */
  private getDayShortName(dayIndex: number): string {
    const shortNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S']
    return shortNames[dayIndex]
  }

  /**
   * Formate la date pour l'affichage dans le tooltip
   */
  private formatDayForTooltip(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }
    return date.toLocaleDateString('fr-FR', options)
  }

  /**
   * Vérifie si deux dates sont le même jour
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    )
  }

  /**
   * Sélectionne un technicien pour afficher ses itinéraires
   */
  selectTechnicianForRoutes(technician: UserModel): void {
    this.selectedTechForRoutes = technician
  }

  /**
   * Récupère les initiales d'un technicien
   */
  getTechnicianInitials(technician: any): string {
    if (technician.initials) return technician.initials
    if (!technician.firstname || !technician.lastname) return '??'
    return (
      technician.firstname.charAt(0).toUpperCase() +
      technician.lastname.charAt(0).toUpperCase()
    )
  }
}
